import { merge } from '../../../helper';

import * as exElement from '../../element';
import type { IElementConfig } from '../../element/types';
import * as exIcon from '../../icon';
import type { IIconConfig } from '../../icon/types';
import * as exLayout from '../../layout/index';

import * as exProgress from '../../../components/progress';
import type { IProgressConfig } from '../../../components/progress/create';
import type { IProgressStackedConfig } from '../../../components/progress/stacked';
import addClass from '../../../general/class/addClass';

import type { IUploadItemTextConfig } from './types';
import createUploadItemText from './utils/createUploadItemText';
import setUploadItemText from './utils/setUploadItemText';

// ----------------------------------------------------------------------------

export interface ICreateUploadItemConfig extends IElementConfig {
    icon?: string;
    title?: string;
    subtitle?: string;
    uploadMiddle?: IElementConfig;
    uploadHeader?: IElementConfig;
    uploadHeaderLeft?: IElementConfig;
    uploadHeaderRight?: IElementConfig;
    uploadIcon?: IIconConfig;
    uploadIconBox?: IElementConfig;
    uploadInfo?: IElementConfig;
    uploadLeft?: IElementConfig;
    uploadPercent?: IUploadItemTextConfig;
    uploadProgress?: IProgressConfig;
    uploadProgressStacked?: IProgressStackedConfig;
    uploadRight?: IElementConfig;
    uploadStatus?: IUploadItemTextConfig;
    uploadSubtitle?: IElementConfig;
    uploadTitle?: IElementConfig;
}

export type TypeBuildReturn = {
    element: HTMLElement;
    elements: {
        element: HTMLElement;
        uploadItemMiddle: HTMLElement;
        uploadItemHeader: HTMLElement;
        uploadItemHeaderLeft: HTMLElement;
        uploadItemHeaderRight: HTMLElement;
        uploadItemIcon: HTMLElement;
        uploadItemIconBox: HTMLElement;
        uploadItemInfo?: HTMLElement;
        uploadItemLeft: HTMLElement;
        uploadItemPercent?: HTMLElement;
        uploadItemProgress?: HTMLElement;
        uploadItemProgressStacked?: HTMLElement;
        uploadItemRight: HTMLElement;
        uploadItemStatus?: HTMLElement;
        uploadItemSubtitle?: HTMLElement;
        uploadItemTitle?: HTMLElement;
    };
    setPercentText: (percent: string) => void;
    setProgress: (value: number) => void;
    setProgressAnimated: (animated: boolean) => void;
    setProgressStatus: (status: string | null) => void;
    setStatus: (status: string) => void;
    setStatusText: (status: string) => void;
    setSubtitleText: (subtitle: string) => void;
    setTitleText: (title: string) => void;
    uploadAbort: (status?: string) => void;
};

// ----------------------------------------------------------------------------

const statusClasses = ['status-uploading', 'status-completed', 'status-canceled', 'status-error', 'status-paused'];
const progressStatusClasses = ['bg-danger', 'bg-success', 'bg-warning', 'bg-primary', 'bg-info'];

// ----------------------------------------------------------------------------

function create(config: ICreateUploadItemConfig = {}): TypeBuildReturn {
    // Default Config
    const defConfig: ICreateUploadItemConfig = {};

    // Merge
    merge(defConfig, config);

    // ------------------------------------------------------------------------

    const build = {
        elements: {},
    } as TypeBuildReturn;

    // ------------------------------------------------------------------------

    const layout = exLayout.create({
        merge: defConfig,
        prefix: 'upload-item',
        layoutLeft: defConfig.uploadLeft,
        layoutMiddle: defConfig.uploadMiddle,
        layoutRight: defConfig.uploadRight,
    });
    build.element = layout.element;
    build.elements.element = layout.element;
    build.elements.uploadItemLeft = layout.elements.layoutLeft;
    build.elements.uploadItemMiddle = layout.elements.layoutMiddle;
    build.elements.uploadItemRight = layout.elements.layoutRight;

    // ------------------------------------------------------------------------

    const elUploadItemIconBox = exElement.create({
        merge: defConfig.uploadIconBox,
        class: {
            new: 'upload-item-icon-box',
        },
    });
    layout.elements.layoutLeft.appendChild(elUploadItemIconBox);
    build.elements.uploadItemIconBox = elUploadItemIconBox;

    const elUploadItemIcon = exIcon.create({
        merge: defConfig.uploadIcon ?? defConfig.icon,
        class: {
            new: 'upload-item-icon',
        },
    });
    elUploadItemIconBox.appendChild(elUploadItemIcon);
    build.elements.uploadItemIcon = elUploadItemIcon;

    // ------------------------------------------------------------------------

    const elUploadItemHeader = exElement.create({
        merge: defConfig.uploadHeader,
        class: {
            new: 'upload-item-header',
        },
    });
    layout.elements.layoutMiddle.appendChild(elUploadItemHeader);
    build.elements.uploadItemHeader = elUploadItemHeader;

    // ------------------------------------------------------------------------

    const elUploadItemHeaderLeft = exElement.create({
        merge: defConfig.uploadHeaderLeft,
        class: {
            new: 'upload-item-header-left',
        },
    });
    elUploadItemHeader.appendChild(elUploadItemHeaderLeft);
    build.elements.uploadItemHeaderLeft = elUploadItemHeaderLeft;

    // ------------------------------------------------------------------------

    if (defConfig.uploadHeaderLeft?.html === undefined) {
        if (defConfig.title !== undefined || defConfig.uploadTitle !== undefined) {
            const elUploadItemTitle = exElement.create({
                merge: defConfig.uploadTitle,
                class: {
                    new: 'upload-item-title',
                },
            });
            if (defConfig.title !== undefined) {
                elUploadItemTitle.setAttribute('title', defConfig.title);
                elUploadItemTitle.textContent = defConfig.title;
            }
            elUploadItemHeaderLeft.appendChild(elUploadItemTitle);
            build.elements.uploadItemTitle = elUploadItemTitle;
        }

        if (defConfig.subtitle !== undefined || defConfig.uploadSubtitle !== undefined) {
            const elUploadItemSubtitle = exElement.create({
                merge: defConfig.uploadSubtitle,
                class: {
                    new: 'upload-item-subtitle',
                },
            });
            if (defConfig.subtitle !== undefined) {
                elUploadItemSubtitle.textContent = defConfig.subtitle;
            }
            elUploadItemHeaderLeft.appendChild(elUploadItemSubtitle);
            build.elements.uploadItemSubtitle = elUploadItemSubtitle;
        }
    }

    // ------------------------------------------------------------------------

    const elUploadItemHeaderRight = exElement.create({
        merge: defConfig.uploadHeaderRight,
        class: {
            new: 'upload-item-header-right',
        },
    });
    elUploadItemHeader.appendChild(elUploadItemHeaderRight);
    build.elements.uploadItemHeaderRight = elUploadItemHeaderRight;

    // ------------------------------------------------------------------------

    if (defConfig.uploadHeaderRight?.html === undefined) {
        const elUploadItemInfo = exElement.create({
            merge: defConfig.uploadInfo,
            class: {
                new: 'upload-item-info',
            },
        });
        elUploadItemHeaderRight.appendChild(elUploadItemInfo);
        build.elements.uploadItemInfo = elUploadItemInfo;

        const elSpanStatus = createUploadItemText(defConfig.uploadStatus, 'upload-item-status', 'Waiting');
        if (elSpanStatus) {
            elUploadItemInfo.appendChild(elSpanStatus);
            build.elements.uploadItemStatus = elSpanStatus;
        }

        const elSpanPercent = createUploadItemText(defConfig.uploadPercent, 'upload-item-percent', '');
        if (elSpanPercent) {
            elUploadItemInfo.appendChild(elSpanPercent);
            build.elements.uploadItemPercent = elSpanPercent;
        }
    }

    // ------------------------------------------------------------------------

    const progressStacked = defConfig.uploadProgressStacked;

    if (progressStacked) {
        const elUploadItemProgressStacked = exProgress.stacked.create({
            merge: progressStacked,
            class: {
                add: 'upload-item-progress-stacked',
            },
            progress: {
                class: {
                    add: 'upload-item-progress',
                },
                attr: {
                    value: '0',
                    max: '100',
                },
            },
        });
        layout.element.appendChild(elUploadItemProgressStacked);
        build.elements.uploadItemProgressStacked = elUploadItemProgressStacked;
    } else if (defConfig.uploadProgress !== undefined) {
        const uploadItemProgress = exProgress.create({
            merge: defConfig.uploadProgress,
            class: {
                add: 'upload-item-progress',
            },
            attr: {
                value: '0',
                max: '100',
            },
        });
        layout.element.appendChild(uploadItemProgress.element);
        build.elements.uploadItemProgress = uploadItemProgress.element;
    }

    // ------------------------------------------------------------------------

    methods(build, defConfig);

    // ------------------------------------------------------------------------

    return build;
}

// ----------------------------------------------------------------------------

function methods(build: TypeBuildReturn, config: ICreateUploadItemConfig): void {
    const elements = build.elements;
    const customStatus = config.uploadStatus?.custom;
    const customPercent = config.uploadPercent?.custom;

    build.setPercentText = function (percent: string): void {
        setUploadItemText(elements.uploadItemPercent, percent, customPercent);
    };

    build.setProgress = function (value: number): void {
        const elBar = elements.uploadItemProgress?.querySelector<HTMLElement>('.progress-bar');
        if (elBar) {
            elBar.style.width = value + '%';
        }
    };

    build.setProgressAnimated = function (animated: boolean): void {
        const elBar = elements.uploadItemProgress?.querySelector<HTMLElement>('.progress-bar');
        if (elBar) {
            elBar.classList.toggle('progress-bar-animated', animated);
        }
    };

    build.setProgressStatus = function (status: string | null): void {
        const elBar = elements.uploadItemProgress?.querySelector<HTMLElement>('.progress-bar');
        if (elBar) {
            progressStatusClasses.forEach((c) => {
                elBar.classList.remove(c);
            });
            if (status !== null && progressStatusClasses.includes(`bg-${status}`)) {
                elBar.classList.add(`bg-${status}`);
            }
        }
    };

    build.setTitleText = function (title: string): void {
        if (elements.uploadItemTitle) {
            elements.uploadItemTitle.textContent = title;
        }
    };

    build.setSubtitleText = function (subtitle: string): void {
        if (elements.uploadItemSubtitle) {
            elements.uploadItemSubtitle.textContent = subtitle;
        }
    };

    build.setStatusText = function (status: string): void {
        setUploadItemText(elements.uploadItemStatus, status, customStatus);
    };

    build.setStatus = function (status: string): void {
        const cls = `status-${status.toLowerCase()}`;
        const elStatus = elements.uploadItemStatus;
        if (elStatus) {
            setUploadItemText(elStatus, status, customStatus);
            statusClasses.forEach((c) => {
                elStatus.classList.remove(c);
            });
            if (statusClasses.includes(cls)) {
                elStatus.classList.add(cls);
            }
        }
        const elPercent = elements.uploadItemPercent;
        if (elPercent) {
            statusClasses.forEach((c) => {
                elPercent.classList.remove(c);
            });
            if (statusClasses.includes(cls)) {
                elPercent.classList.add(cls);
            }
        }
        elements.element.classList.remove('border-warning');
        if (cls === 'status-paused') {
            elements.element.classList.add('border-warning');
        }
    };

    build.uploadAbort = function (status?: string): void {
        elements.element.classList.remove('border-warning');
        addClass(elements.element, 'border-danger');
        const elStatus = elements.uploadItemStatus;
        if (elStatus) {
            setUploadItemText(elStatus, status ?? 'Canceled', customStatus);
            statusClasses.forEach((c) => {
                elStatus.classList.remove(c);
            });
            elStatus.classList.add('status-canceled');
        }
        const elPercent = elements.uploadItemPercent;
        if (elPercent) {
            statusClasses.forEach((c) => {
                elPercent.classList.remove(c);
            });
            elPercent.classList.add('status-canceled');
        }
        const elBar = elements.uploadItemProgress?.querySelector<HTMLElement>('.progress-bar');
        if (elBar) {
            elBar.classList.remove('progress-bar-animated');
            progressStatusClasses.forEach((c) => {
                elBar.classList.remove(c);
            });
            elBar.classList.add('bg-danger');
        }
    };
}

// ----------------------------------------------------------------------------

export default create;
